import store from 'shared/store'
import loadCatalogs from 'views/containers/browser/utils/loadCatalogs'
import toggleBoolStringAttr from 'views/utils/toggleBoolStringAttribute'

import { ARIA_HIDDEN, OVERLAY_SELECTOR } from 'views/assets/scripts/consts'
import {
  SELECTOR_DESKTOP_CONTAINER,
  SELECTOR_DESKTOP_INPUT,
  SELECTOR_MOBILE_CONTAINER,
  SELECTOR_MOBILE_INPUT,
  SELECTOR_DUMMY_SEARCH_BOX,
  SELECTOR_MOBILE_SEARCH_ICON,
  SELECTOR_DUMMY_SEARCH_BOX_INPUT
} from './consts'

import bindDummyHeader from 'views/assets/scripts/mobileHeaderDummySearchBox'

export default () => {
  /** @type HTMLElement */
  const overlay = document.querySelector(OVERLAY_SELECTOR)

  const desktopInputs = [...document.querySelectorAll(SELECTOR_DESKTOP_INPUT)]

  /** @type {HTMLInputElement} */
  const mobileInput = document.querySelector(SELECTOR_MOBILE_INPUT)
  /** @type {HTMLInputElement} */
  const mobileDummySearchBoxInput = document.querySelector(
    SELECTOR_DUMMY_SEARCH_BOX_INPUT
  )
  /** @type {HTMLElement} */
  const mobileDummySearchBox = document.querySelector(SELECTOR_DUMMY_SEARCH_BOX)
  /** @type {HTMLElement} */
  const mobileSearchIcon = document.querySelector(SELECTOR_MOBILE_SEARCH_ICON)
  /** @type {Array<HTMLElement>} */
  const mobileSearchTriggers = [mobileDummySearchBox, mobileSearchIcon].filter(
    Boolean
  )
  /** @type {Array<HTMLInputElement>} */
  const mobileSearchInputs = [mobileDummySearchBoxInput, mobileInput].filter(
    Boolean
  )

  const hasMobile = Boolean(
    mobileSearchInputs.length || mobileSearchTriggers.length
  )
  const hasDesktop = Boolean(desktopInputs.length)

  let isLoadedDesktop = false
  let desktopInput

  const showOverlay = e => {
    desktopInput = e.target

    toggleBoolStringAttr(overlay, ARIA_HIDDEN, false)
    toggleBoolStringAttr(
      desktopInput.closest(SELECTOR_DESKTOP_CONTAINER),
      'aria-focused',
      true
    )
  }

  const focusAndOpenKeyboard = (el, timeout = 100) => {
    if (el) {
      // Align temp input element approx. to be where the input element is
      const tempEl = document.createElement('input')
      tempEl.style.position = 'absolute'
      tempEl.style.top = `${el.offsetTop + 7}px`
      tempEl.style.left = `${el.offsetLeft}px`
      tempEl.style.height = '0'
      tempEl.style.opacity = '0'
      // Put this temp element as a child of the page <body> and focus on it
      document.body.appendChild(tempEl)
      tempEl.focus()

      // The keyboard is open. Now do a delayed focus on the target element
      setTimeout(() => {
        el.focus()
        el.click()
        // Remove the temp element
        document.body.removeChild(tempEl)
      }, timeout)
    }
  }

  const hideOverlay = () => {
    if (isLoadedDesktop) {
      // SearchBoxDesktop has its own handler
      desktopInput.removeEventListener('blur', hideOverlay)
      return
    }

    toggleBoolStringAttr(overlay, ARIA_HIDDEN, true)
    toggleBoolStringAttr(
      desktopInput.closest(SELECTOR_DESKTOP_CONTAINER),
      'aria-focused',
      false
    )
  }

  const initSearchAutocomplete = async ({ input, container, isMobile }) => {
    if (!container) {
      return
    }

    const {
      publicConfig: { locale }
    } = store.getPublicRuntimeConfig()
    const catalogs = await loadCatalogs(locale)
    const hydrateSearchBoxComponent = await import(
      /* webpackChunkName: "SearchBoxContainerWithHydration", webpackPrefetch: true */
      `./SearchBoxContainerWithHydration`
    )

    hydrateSearchBoxComponent.default({
      container,
      locale,
      catalogs,
      isMobile,
      query: input.value
    })

    isLoadedDesktop = !isMobile

    if (isLoadedDesktop) {
      showOverlay({ target: input })
    }
  }

  const loadSearchAutocompleteDesktop = () => {
    initSearchAutocomplete({
      container: desktopInput.closest(SELECTOR_DESKTOP_CONTAINER),
      isMobile: false,
      input: desktopInput
    })

    desktopInput.removeEventListener('input', loadSearchAutocompleteDesktop)
  }

  const loadSearchAutocompleteMobile = () => {
    initSearchAutocomplete({
      container: document.querySelector(SELECTOR_MOBILE_CONTAINER),
      isMobile: true,
      input: mobileInput
    })

    mobileSearchTriggers.forEach(trigger => {
      trigger.removeEventListener('click', loadSearchAutocompleteMobile)
    })
  }

  const syncMobileSearchInputs = event =>
    mobileSearchInputs.forEach(input => {
      // eslint-disable-next-line no-param-reassign
      input.value = event.target.value
    })

  const bindDesktopEvents = () => {
    if (!hasDesktop) {
      return
    }

    desktopInputs.forEach(input => {
      input.addEventListener('input', loadSearchAutocompleteDesktop)
      input.addEventListener('focus', showOverlay)
      input.addEventListener('blur', hideOverlay)
    })
  }

  const bindMobileEvents = () => {
    mobileSearchTriggers.forEach(trigger => {
      trigger.addEventListener('click', loadSearchAutocompleteMobile)
      trigger.addEventListener('click', () => focusAndOpenKeyboard(mobileInput))
    })

    mobileSearchInputs.forEach(input => {
      input.addEventListener('search', syncMobileSearchInputs)
    })

    bindDummyHeader(mobileDummySearchBox)
  }

  const triggerUpdate = () => {
    const { activeElement } = document

    desktopInputs.forEach(input => {
      if (input === activeElement) {
        activeElement.dispatchEvent(new Event('focus'))
        activeElement.dispatchEvent(new Event('input'))
      }
    })

    if (mobileInput === activeElement) {
      activeElement.dispatchEvent(new Event('click'))
    }
  }

  if (hasMobile) {
    bindMobileEvents()
  }
  if (hasDesktop) {
    bindDesktopEvents()
  }
  if (hasMobile || hasDesktop) {
    triggerUpdate()
  }
}
